/**
 * Утилиты нормализации JSON-ответов ABP: некоторые серверы возвращают
 * Pascal-case, некоторые — camelCase. Вытаскиваем значение по обоим ключам.
 */
type UnknownRecord = Record<string, unknown>

/** Возвращает непустую строку из любого из двух ключей (camel/pascal). */
export const pickString = (
  raw: UnknownRecord,
  camel: string,
  pascal: string,
): string | null | undefined => {
  const a = raw[camel]
  const b = raw[pascal]
  if (typeof a === "string" && a.trim() !== "") return a
  if (typeof b === "string" && b.trim() !== "") return b
  return undefined
}

const leadingLowerKey = (key: string): string => {
  if (!key) return key
  const c0 = key.charCodeAt(0)
  /** ASCII A–Z */
  if (c0 < 65 || c0 > 90) return key
  return key[0].toLowerCase() + key.slice(1)
}

/**
 * Рекурсивно приводит ключи объектов к camelCase-стилю с ведущей строчной буквой
 * (как у System.Text.Json), если API отдал PascalCase: `Profile`, `Educations`, `Institution`.
 */
export const deepLeadingLowerKeys = <T>(input: unknown): T => {
  if (input === null || input === undefined) return input as T
  if (Array.isArray(input)) return input.map((item) => deepLeadingLowerKeys(item)) as T
  if (typeof input !== "object") return input as T
  const obj = input as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(obj)) {
    const nk = leadingLowerKey(key)
    out[nk] = deepLeadingLowerKeys(obj[key])
  }
  return out as T
}

/**
 * Часть контроллеров ABP отдаёт DTO внутри обёртки `{ result: ... }` / `{ data: ... }`
 * (camelCase или PascalCase ключа). Без распаковки на верхнем уровне нет `profile` и списков.
 */
export const unwrapAbpApplicationResult = (raw: unknown): unknown => {
  if (typeof raw !== "object" || raw === null) return raw
  const o = raw as UnknownRecord
  const inner = o.result ?? o.Result ?? o.data ?? o.Data
  if (inner !== undefined && inner !== null && typeof inner === "object" && !Array.isArray(inner)) {
    return inner
  }
  return raw
}
